import { createParser, parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import { useCallback, useMemo } from 'react'
import { type UIConsignmentStatus } from '~/entities/consignment'
import { DEFAULT_PAGE_SIZE, TABLE_COLUMNS } from '../config'
import { getDisabledFilters } from '../lib'
import type { ConsignmentFilters } from './types'
import { usePaginationSelectors } from './use-pagination-store'
import { usePinnedFiltersSelectors } from './use-pinned-filters-store'

const emptyFilters: ConsignmentFilters = {
  pageSize: DEFAULT_PAGE_SIZE,
  assetIdQuery: '',
  status: [],
  sortBy: TABLE_COLUMNS.DATE_RECEIVED,
  sortOrder: 'asc',
}

const initialFilters: ConsignmentFilters = {
  pageSize: DEFAULT_PAGE_SIZE,
  assetIdQuery: '',
  status: ['awaiting'],
  sortBy: TABLE_COLUMNS.DATE_RECEIVED,
  sortOrder: 'asc',
}

const statusParser = createParser({
  parse: (query: string | null) => {
    if (!query) return null
    return query === 'all' ? [] : (query.split(',') as UIConsignmentStatus[])
  },
  serialize: (value: UIConsignmentStatus[]) => {
    return value.length === 0 ? 'all' : value.join(',')
  },
})

export function useFilters() {
  const [filters, setFilters] = useQueryStates(
    {
      pageSize: parseAsInteger.withDefault(initialFilters.pageSize),
      assetIdQuery: parseAsString.withDefault(initialFilters.assetIdQuery),
      status: statusParser.withDefault(initialFilters.status),
      sortBy: parseAsString.withDefault(initialFilters.sortBy),
      sortOrder: parseAsString.withDefault(initialFilters.sortOrder),
    },
    {
      urlKeys: {
        assetIdQuery: 'q',
      },
    },
  )
  const { resetPagination } = usePaginationSelectors({ pageSize: filters.pageSize })

  const disabledFilters = useMemo(() => getDisabledFilters(filters), [filters])

  const updateFilters = useCallback(
    (newFilters: Partial<ConsignmentFilters>) => {
      const { assetIdQuery, ...restFilters } = newFilters

      // Backend doesn't support simultaneous filters, so we need to reset the others
      if (assetIdQuery !== undefined) {
        setFilters({
          assetIdQuery,
          status: [],
        })
      } else if (newFilters.status !== undefined) {
        setFilters({
          assetIdQuery: '',
          status: newFilters.status,
        })
      } else {
        setFilters({
          ...restFilters,
        })
      }
      resetPagination()
    },
    [resetPagination, setFilters],
  )

  const isDirty = useMemo(() => {
    // assetIdQuery is not counted as filter by business logic, but it's still a filter by implementation
    return filters.status.length > 0
  }, [filters.status])

  const isAssetIdQueryDirty = useMemo(() => {
    return filters.assetIdQuery !== initialFilters.assetIdQuery
  }, [filters.assetIdQuery])

  const resetFilters = useCallback(() => {
    updateFilters(emptyFilters)
  }, [updateFilters])

  const resetToInitialFilters = useCallback(() => {
    setFilters(initialFilters)
    resetPagination()
  }, [resetPagination, setFilters])

  return {
    filters,
    setFilters: updateFilters,
    resetFilters,
    resetToInitialFilters,
    isDirty,
    isAssetIdQueryDirty,
    disabledFilters,
  }
}

export const usePinnedFilters = () => {
  const { pinnedFilters, setPinnedFilters } = usePinnedFiltersSelectors()
  return { pinnedFilters, setPinnedFilters }
}
